import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Image, Spinner } from 'react-bootstrap';
import { Pen, People, Newspaper } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import DeskCarousel from '../carousel/DeskCarousel';
import './MexicoStatePage.css';

// Import from your Mexico feedsConfig
import { getMexicanStateFeedsByBranch, getMexicanStateInfo, getMexicanStateUpdateFeed } from '../../utils/mexicoFeedsConfig';
import MexicanHomeStateButton from '../notifications/MexicanHomeStateButton';

const JudicialIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
    <path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/>
  </svg>
);

const MexicoStatePage = () => {
  const { t } = useTranslation('common');
  const { stateName } = useParams();
  const stateKey = stateName ? stateName.toLowerCase() : null;
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Look up Mexico state info
  const currentState = stateKey ? getMexicanStateInfo(stateKey) : null;
  
  if (!currentState) {
    return (
      <div className="mexico-state-page-root d-flex justify-content-center align-items-center min-vh-100">
        <div>
          <Spinner id='spinner' animation="grow" variant="light" />
          <p style={{ color: 'var(--text-primary)' }}>{t('common:stateNotFound', 'State not found: {{stateName}}', { stateName })}</p>
        </div>
      </div>
    );
  }
  
  // Create Mexico state-specific feeds (only if we have a valid stateKey)
  const stateFeeds = stateKey ? {
    legislative: getMexicanStateFeedsByBranch(stateKey, 'legislative'),
    executive: getMexicanStateFeedsByBranch(stateKey, 'executive'),
    judicial: getMexicanStateFeedsByBranch(stateKey, 'judicial')
  } : null;
  const updateFeed = stateKey ? getMexicanStateUpdateFeed(stateKey) : null;

  // Transform state feeds into the format DeskCarousel expects
  const transformFeedsForCarousel = (feeds, itemCount = 5) => {
    if (!feeds || feeds.length === 0) return [];
    
    const result = [];
    feeds.forEach(feed => {
      for (let i = 0; i < itemCount; i++) {
        result.push({
          type: 'normal',
          id: `${feed.id}-${i}`,
          title: feed.name,
          apiEndpoint: feed.apiParams.replace(/skip=\d+/, `skip=${i}`),
          feedName: feed.name,
          branch: feed.feedBranch,
          topic: feed.feedTopic,
          buttonName: feed.buttonName,
          viewAllLink: null
        });
      }
    });
    return result;
  };

  // MOBILE LAYOUT
  if (isMobile) {
    return (
      <div id='homebg' className="mexico-state-page-root">
        <div style={{ marginTop: '45px', paddingTop: '5px' }}>
       
          {/* State Header */}
 

          {/* Updates Section */}
          {updateFeed && updateFeed.length > 0 && (
            <div className="state-branch-section">
              <div className="branch-header">
                <div className="branch-title">
                  <Newspaper /> {t('common:updates', 'Updates')}
                </div>
              </div>
              <MobileFeedCarousel feeds={updateFeed} />
            </div>
          )}

          {/* Legislative Section */}
          {stateFeeds?.legislative?.length > 0 && (
            <div className="state-branch-section">
              <div className="branch-header">
                <div className="branch-title">
                  <Pen /> {t('common:section_legislative', 'Legislative')}
                </div>
              </div>
              <MobileFeedCarousel feeds={stateFeeds.legislative} />
            </div>
          )}

          {/* Executive Section */}
          {stateFeeds?.executive?.length > 0 && (
            <div className="state-branch-section">
              <div className="branch-header">
                <div className="branch-title">
                  <People /> {t('common:section_executive', 'Executive')}
                </div>
              </div>
              <MobileFeedCarousel feeds={stateFeeds.executive} />
            </div>
          )}

          {/* Judicial Section */}
          {stateFeeds?.judicial?.length > 0 && (
            <div className="state-branch-section">
              <div className="branch-header">
                <div className="branch-title">
                  <JudicialIcon /> {t('common:section_judicial', 'Judicial')}
                </div>
              </div>
              <MobileFeedCarousel feeds={stateFeeds.judicial} />
            </div>
          )}

          {/* Empty state */}
          {(!updateFeed?.length &&
            !stateFeeds?.legislative?.length && 
            !stateFeeds?.executive?.length && 
            !stateFeeds?.judicial?.length) && (
            <div className="empty-state">
              <p>{t('common:noFeedsAvailable', 'No feeds available for {{title}}', { title: currentState.fullName })}</p>
              <p className="empty-state-subtext">
                {t('common:stateCodeLabel', 'State code: {{code}}', { code: currentState.code })}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // DESKTOP LAYOUT - Updates centered above three branches
  return (
    <div id='homebg' className="mexico-state-page-root">
      <br />
      <br />
      <Container fluid>
        {/* State Header with flag and Home State Button */}
        {/* <Row className="mb-3">
          <Col>
            <div className="d-flex align-items-center justify-content-between">
              <h5 id='stateFlagHeadingMobile'>
                <Image 
                  id='stateFlagMobile' 
                  src={currentState.flagImage} 
                  alt={`${currentState.fullName} flag`} 
                />
                {currentState.fullName}
              </h5>
              <MexicanHomeStateButton
                stateKey={stateKey} 
                stateName={currentState.fullName}
              />
            </div>
          </Col>
        </Row> */}

        {/* Updates Row - Centered above branches */}
        {updateFeed && updateFeed.length > 0 && (
          <Row className="mb-4 justify-content-center">
            <Col md={6} lg={5}>
              <DeskCarousel 
                title={t('common:updates', 'Updates')}
                icon={<Newspaper />}
                sections={transformFeedsForCarousel(updateFeed)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            </Col>
          </Row>
        )}
        
        {/* Branches Row - Three columns */}
        <Row id="branchesRow">
          {/* Legislative Section */}
          <Col md={4}>
            {stateFeeds?.legislative?.length > 0 ? (
              <DeskCarousel 
                title={t('common:section_legislative', 'Legislative')}
                icon={<Pen />}
                sections={transformFeedsForCarousel(stateFeeds.legislative)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            ) : (
              <div className="branch-section">
                <h5 className="branch-title">
                  <Pen /> {t('common:section_legislative', 'Legislative')}
                </h5>
                <div className="coming-soon-box">
                  <p>{t('common:comingSoon', 'Coming Soon')}</p>
                </div>
              </div>
            )}
          </Col>
          
          {/* Executive Section */}
          <Col md={4}>
            {stateFeeds?.executive?.length > 0 ? (
              <DeskCarousel
                title={t('common:section_executive', 'Executive')}
                icon={<People />}
                sections={transformFeedsForCarousel(stateFeeds.executive)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            ) : (
              <div className="branch-section">
                <h5 className="branch-title">
                  <People /> {t('common:section_executive', 'Executive')}
                </h5>
                <div className="coming-soon-box">
                  <p>{t('common:comingSoon', 'Coming Soon')}</p>
                </div>
              </div>
            )}
          </Col>
          
          {/* Judicial Section */}
          <Col md={4}>
            {stateFeeds?.judicial?.length > 0 ? (
              <DeskCarousel
                title={t('common:section_judicial', 'Judicial')}
                icon={<JudicialIcon />}
                sections={transformFeedsForCarousel(stateFeeds.judicial)}
                viewAllLink={null}
                intervalDuration={30000}
                disableNavigation={true}
              />
            ) : (
              <div className="branch-section">
                <h5 className="branch-title">
                  <JudicialIcon /> {t('common:section_judicial', 'Judicial')}
                </h5>
                <div className="coming-soon-box">
                  <p>{t('common:comingSoon', 'Coming Soon')}</p>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MexicoStatePage;